import { AfterViewInit, Directive, ElementRef, HostBinding, Input, OnDestroy, Renderer2 } from '@angular/core';

@Directive({
  selector: '[appReveal]'
})
export class RevealOnScrollDirective implements AfterViewInit, OnDestroy {
  @Input() revealDelay = 0;
  @Input() revealVariant: 'up' | 'image' | 'scale' | 'card' = 'up';

  // Host bindings are applied during Angular's first render. Adding the
  // hidden class later in ngAfterViewInit caused an unintended fade OUT from
  // initially visible content before the actual scroll reveal could run.
  @HostBinding('class.reveal-on-scroll') readonly animate = true;
  @HostBinding('class.reveal-image') get imageVariant(): boolean { return this.revealVariant === 'image'; }
  @HostBinding('class.reveal-scale') get scaleVariant(): boolean { return this.revealVariant === 'scale'; }
  @HostBinding('class.reveal-card') get cardVariant(): boolean { return this.revealVariant === 'card'; }

  private observer?: IntersectionObserver;
  private removeScrollListener?: () => void;
  private isRevealed = false;

  constructor(private elementRef: ElementRef<HTMLElement>, private renderer: Renderer2) {}

  ngAfterViewInit(): void {
    const element = this.elementRef.nativeElement;
    this.renderer.setStyle(element, '--reveal-delay', `${this.revealDelay}ms`);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      this.reveal();
      return;
    }

    this.observer = new IntersectionObserver(entries => {
      // A fast wheel scroll, programmatic jump or restored scroll position can
      // skip an element without ever satisfying the previous 12% threshold.
      // A skipped element must not stay permanently invisible.
      if (entries.some(entry => entry.isIntersecting)) {
        this.reveal();
      } else {
        this.revealIfPassed();
      }
    }, { rootMargin: '0px 0px -5% 0px', threshold: 0 });

    // IntersectionObserver might not report a fast jump where the element was
    // outside the viewport both before and after the scroll. Catch that case
    // explicitly and remove the listener as soon as the element is revealed.
    this.removeScrollListener = this.renderer.listen('window', 'scroll', () => this.revealIfPassed());
    this.observer.observe(element);
    this.revealIfPassed();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.removeScrollListener?.();
    this.removeScrollListener = undefined;
  }

  private revealIfPassed(): void {
    if (!this.isRevealed && this.elementRef.nativeElement.getBoundingClientRect().bottom <= 0) {
      this.renderer.setStyle(this.elementRef.nativeElement, '--reveal-delay', '0ms');
      this.reveal();
    }
  }

  private reveal(): void {
    if (this.isRevealed) {
      return;
    }
    this.isRevealed = true;
    this.renderer.addClass(this.elementRef.nativeElement, 'is-visible');
    this.observer?.disconnect();
    this.removeScrollListener?.();
    this.removeScrollListener = undefined;
  }
}
